import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Divider, Chip, Stack,
  TextField, MenuItem, Skeleton, alpha, useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import 'dayjs/locale/th';
import PropTypes from 'prop-types';
import * as actions from '../../redux/actions';

dayjs.extend(buddhistEra);
dayjs.locale('th');

const MODULE = 'MANAGEMENT';

const RESOURCE_TYPE_OPTIONS = [
  { value: '', label: 'ทั้งหมด' },
  { value: 'พนักงาน', label: 'พนักงาน' },
  { value: 'วันหยุด', label: 'วันหยุด' },
  { value: 'แผนก', label: 'แผนก' },
  { value: 'ประเภทบทบาท', label: 'ประเภทบทบาท' },
  { value: 'ผู้ใช้งาน', label: 'ผู้ใช้งาน' },
  { value: 'ประเภทการจ่ายเงิน', label: 'ประเภทการจ่ายเงิน' },
  { value: 'ประเภทรายการ', label: 'ประเภทรายการ' },
  { value: 'ตั้งค่า', label: 'ตั้งค่า' },
  { value: 'นักพัฒนา', label: 'นักพัฒนา' },
];

const ACTION_OPTIONS = [
  { value: '', label: 'ทั้งหมด' },
  { value: 'CREATE', label: '✏️ สร้างรายการ' },
  { value: 'UPDATE', label: '🔧 แก้ไขข้อมูล' },
  { value: 'DELETE', label: '🗑️ ลบรายการ' },
  { value: 'READ', label: '📖 อ่านข้อมูล' },
  { value: 'VIEW', label: '👁️ ดูรายละเอียด' },
  { value: 'APPROVE', label: '✅ อนุมัติเอกสาร' },
  { value: 'REJECT', label: '❌ ปฏิเสธเอกสาร' },
  { value: 'EXPORT', label: '📊 Export ข้อมูล' },
  { value: 'IMPORT', label: '📥 Import ข้อมูล' },
  { value: 'PRINT', label: '🖨️ พิมพ์เอกสาร' },
  { value: 'VOID', label: '🚫 ยกเลิกเอกสาร' },
  { value: 'DOWNLOAD', label: '⬇️ ดาวน์โหลด' },
  { value: 'UPLOAD', label: '⬆️ อัพโหลด' },
  { value: 'SEARCH', label: '🔍 ค้นหา' },
  { value: 'SUBMIT', label: '📤 ส่งเอกสาร' },
  { value: 'COMPLETE', label: '✔️ เสร็จสิ้น' },
  { value: 'CANCEL', label: '⛔ ยกเลิก' },
  { value: 'RESTORE', label: '♻️ กู้คืน' },
];

const ACTION_CHIP = {
  CREATE: { label: '✏️ สร้าง', color: 'success' },
  UPDATE: { label: '🔧 แก้ไข', color: 'warning' },
  DELETE: { label: '🗑️ ลบ', color: 'error' },
  READ: { label: '📖 อ่าน', color: 'info' },
  VIEW: { label: '👁️ ดู', color: 'info' },
  APPROVE: { label: '✅ อนุมัติ', color: 'success' },
  REJECT: { label: '❌ ปฏิเสธ', color: 'error' },
  EXPORT: { label: '📊 Export', color: 'primary' },
  IMPORT: { label: '📥 Import', color: 'primary' },
  PRINT: { label: '🖨️ พิมพ์', color: 'default' },
  VOID: { label: '🚫 ยกเลิก', color: 'error' },
  DOWNLOAD: { label: '⬇️ ดาวน์โหลด', color: 'primary' },
  UPLOAD: { label: '⬆️ อัพโหลด', color: 'primary' },
  SEARCH: { label: '🔍 ค้นหา', color: 'default' },
  SUBMIT: { label: '📤 ส่ง', color: 'info' },
  COMPLETE: { label: '✔️ เสร็จสิ้น', color: 'success' },
  CANCEL: { label: '⛔ ยกเลิก', color: 'warning' },
  RESTORE: { label: '♻️ กู้คืน', color: 'info' },
};

const RESOURCE_LABEL = {
  'พนักงาน': 'พนักงาน',
  'วันหยุด': 'วันหยุด',
  'แผนก': 'แผนก',
  'ประเภทบทบาท': 'ประเภทบทบาท',
  'ผู้ใช้งาน': 'ผู้ใช้งาน',
  'ประเภทการจ่ายเงิน': 'ประเภทการจ่ายเงิน',
  'ประเภทรายการ': 'ประเภทรายการ',
  'ตั้งค่า': 'ตั้งค่า',
  'นักพัฒนา': 'นักพัฒนา',
  department: 'แผนก',
  roleType: 'ประเภทบทบาท',
  holiday: 'วันหยุด',
  payType: 'ประเภทการจ่ายเงิน',
  transectionType: 'ประเภทรายการ',
};

function ManagementActivityLog({ title }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const activityLog = useSelector((state) => state.activityLog);

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [submenu, setSubmenu] = useState('');
  const [action, setAction] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchLogs = useCallback(() => {
    dispatch(actions.activityLogAll({ module: MODULE, submenu, action, startDate, endDate, page, size }));
  }, [submenu, action, startDate, endDate, page, size]);

  useEffect(() => {
    fetchLogs();
    return () => {};
  }, [fetchLogs]);

  const handleChangePage = (_, newPage) => setPage(newPage + 1);
  const handleChangeRowsPerPage = (e) => { setSize(+e.target.value); setPage(1); };
  const handleClear = () => { setSubmenu(''); setAction(''); setStartDate(''); setEndDate(''); setPage(1); };

  const renderSkeleton = () => (
    <TableBody>
      {Array.from({ length: 10 }).map((_, i) => (
        <TableRow key={i}>
          <TableCell><Skeleton variant="text" width={130} /></TableCell>
          <TableCell><Skeleton variant="text" width={90} /></TableCell>
          <TableCell><Skeleton variant="rounded" width={55} height={24} /></TableCell>
          <TableCell><Skeleton variant="rounded" width={70} height={24} /></TableCell>
          <TableCell><Skeleton variant="text" width="80%" /></TableCell>
        </TableRow>
      ))}
    </TableBody>
  );

  const renderRows = () => {
    if (!activityLog?.rows?.length) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={5}>
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography color="text.secondary">ไม่พบข้อมูล log</Typography>
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }
    return (
      <TableBody>
        {activityLog.rows.map((row, i) => {
          const chip = ACTION_CHIP[row.action] || { label: row.action, color: 'default' };
          return (
            <TableRow
              key={row._id || i}
              sx={{
                '&:nth-of-type(odd)': { bgcolor: alpha(theme.palette.background.paper, 0.5) },
                '&:hover': { bgcolor: alpha(theme.palette.primary.light, 0.04) },
              }}
            >
              <TableCell><Typography variant="body2">{dayjs(row.createdAt).format('DD MMM BBBB HH:mm')}</Typography></TableCell>
              <TableCell><Typography variant="body2">{row.actorName || row.actorUsername || '-'}</Typography></TableCell>
              <TableCell><Chip size="small" label={chip.label} color={chip.color} /></TableCell>
              <TableCell><Chip size="small" label={RESOURCE_LABEL[row.submenu] || row.submenu || RESOURCE_LABEL[row.resourceType] || row.resourceType} variant="outlined" /></TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ maxWidth: 400, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {row.summary || '-'}
                </Typography>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    );
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 3 }}>{title}</Typography>

      <Card elevation={0} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" alignItems="center">
          <TextField select size="small" label="หมวดหมู่" sx={{ minWidth: 180 }}
            value={submenu} onChange={(e) => setSubmenu(e.target.value)}>
            {RESOURCE_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="การกระทำ" sx={{ minWidth: 120 }}
            value={action} onChange={(e) => setAction(e.target.value)}>
            {ACTION_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          <TextField size="small" label="วันที่เริ่ม" type="date" sx={{ minWidth: 155 }}
            value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" label="วันที่สิ้นสุด" type="date" sx={{ minWidth: 155 }}
            value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Stack direction="row" spacing={1}>
            <Chip label="ค้นหา" color="primary" clickable onClick={() => { setPage(1); fetchLogs(); }} />
            <Chip label="ล้าง" variant="outlined" clickable onClick={handleClear} />
          </Stack>
        </Stack>
      </Card>

      <Card elevation={0} sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                <TableCell><Typography variant="subtitle2" fontWeight="bold">วันที่-เวลา</Typography></TableCell>
                <TableCell><Typography variant="subtitle2" fontWeight="bold">ผู้ทำรายการ</Typography></TableCell>
                <TableCell><Typography variant="subtitle2" fontWeight="bold">การกระทำ</Typography></TableCell>
                <TableCell><Typography variant="subtitle2" fontWeight="bold">หมวด</Typography></TableCell>
                <TableCell><Typography variant="subtitle2" fontWeight="bold">รายละเอียด</Typography></TableCell>
              </TableRow>
            </TableHead>
            {activityLog.isLoading ? renderSkeleton() : renderRows()}
          </Table>
        </TableContainer>
        <Divider />
        <TablePagination
          component="div"
          count={activityLog?.count || 0}
          page={page - 1}
          rowsPerPage={size}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="แถวต่อหน้า:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count !== -1 ? count : `มากกว่า ${to}`}`}
        />
      </Card>
    </Box>
  );
}

ManagementActivityLog.propTypes = { title: PropTypes.string };
ManagementActivityLog.defaultProps = { title: 'บันทึกกิจกรรม (ตั้งค่าระบบ)' };

export default ManagementActivityLog;
